import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type PublicClip, ShortUrl } from '@air/api';
import { InlineInput, type InlineInputProps } from '@air/component-inline-input';
import {
  contentCollectionClipsInvalidFieldsSelector,
  contentCollectionFormSelector,
  contentCollectionSessionIdSelector,
  updateContentCollectionClipsAction,
} from '@air/feature-content-collection/redux';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { EditableMultilineTextTriggerButton } from '~/_components/InlineInput/EditableMultilineTextTriggerButton';
import { type AirState } from '~/store/types';

const EDITABLE_DESCRIPTION_HEIGHT = 32;

export type ContentCollectionGalleryAssetDescriptionProps = {
  clip: PublicClip;
};

export const ContentCollectionGalleryAssetDescription = memo(
  ({ clip }: ContentCollectionGalleryAssetDescriptionProps) => {
    const dispatch = useDispatch();
    const sessionId = useSelector(contentCollectionSessionIdSelector);
    const form = useSelector(contentCollectionFormSelector);
    const { shortId } = useShortIdContext();
    const [isEditing, setIsEditing] = useState(false);
    const invalidFields = useSelector((st: AirState) => contentCollectionClipsInvalidFieldsSelector(st, [clip.id]));

    const isInvalid = invalidFields.some((f) => f.type === 'description');

    const [value, setValue] = useState(clip.description);

    useEffect(() => {
      setValue(clip.description);
    }, [clip.description]);

    const onSubmit = useCallback<Required<InlineInputProps>['onSubmit']>(
      (newDescription) => {
        if (!sessionId) {
          return;
        }

        dispatch(
          updateContentCollectionClipsAction({
            clips: [
              {
                ...clip,
                description: newDescription ?? '',
              },
            ],
          }),
        );

        // biome-ignore lint/nursery/noFloatingPromises: PLA-1724 — pre-existing debt, not introduced by the color pairing
        ShortUrl.batchUpdateFormAssets(shortId, form.id, {
          formSessionId: sessionId,
          update: {
            description: !newDescription ? null : newDescription,
          },
          assetIds: [clip.assetId],
        });
      },
      [clip, dispatch, form.id, sessionId, shortId],
    );

    const placeholder = isInvalid ? 'Description required' : 'Add a description here…';

    return (
      <div
        data-testid="EDITABLE_CONTENT_COLLECTION_ASSET_DESCRIPTION"
        style={{
          height: EDITABLE_DESCRIPTION_HEIGHT,
        }}
      >
        {isEditing ? (
          <InlineInput
            data-testid="EDITABLE_CONTENT_COLLECTION_ASSET_DESCRIPTION_INPUT"
            label="Asset description"
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
            onSubmit={(value) => {
              setIsEditing(false);
              onSubmit(value);
            }}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
            onCancel={() => {
              setValue(clip.description);
              setIsEditing(false);
            }}
            onChange={setValue}
            className="-ml-px h-full pt-px"
            inputClassName="text-14 ads:text-secondary text-gray-11 py-1 px-1 -mt-0.5"
            defaultValue={value}
            placeholder={placeholder}
          />
        ) : (
          <EditableMultilineTextTriggerButton
            placeholder={placeholder}
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the color pairing
            onClick={() => setIsEditing(true)}
            className={cn('size-full px-1 text-14', isInvalid && 'ads:ring-error ring-1 ring-red-9')}
            value={value}
          />
        )}
      </div>
    );
  },
);

ContentCollectionGalleryAssetDescription.displayName = 'ContentCollectionGalleryAssetDescription';
